import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";

import {
	passwordlessValidator,
	validator
} from "lib/build/forms/common/profile";
import { Value } from "lib/build/schema";

import { Button } from "src/common/components/Button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from "src/common/components/Card";
import {
	Fieldset,
	Form,
	FormBody,
	FormFooter,
	Legend
} from "src/common/components/Form";
import { SplitColumn, SplitRow } from "src/common/components/Grid";
import { FormPage } from "src/common/components/Page";
import { FullPagePreloader } from "src/common/components/Preloader";
import { AuthType, useAuthState } from "src/features/auth";
import {
	FieldData,
	FieldsetData,
	FieldsetRenderer,
	FormContextProvider
} from "src/features/forms";
import {
	genericMessages,
	languageMessages,
	occupationMessages
} from "src/messages/generic";
import { messages } from "src/views/user-profile/messages";
import { useForm } from "src/views/user-profile/useForm";
import { useInitialValue } from "src/views/user-profile/useInitialValue";

import { useAuthType } from "./useAuthType";

const errors = defineMessages({
	firstNameRequired: {
		id: "form.user_profile.first_name.error.required",
		defaultMessage: "A keresztnév kötelező."
	},
	lastNameRequired: {
		id: "form.user_profile.last_name.error.required",
		defaultMessage: "A vezetéknév kötelező."
	},
	phoneNumberRequired: {
		id: "form.user_profile.phone_number.error.required",
		defaultMessage: "A telefonszám kötelező."
	},
	phoneNumberFormat: {
		id: "form.user_profile.phone_number.error.format",
		defaultMessage: "A telefonszám formátuma nem megfelelő."
	},
	occupationRequired: {
		id: "form.user_profile.occupation.error.required",
		defaultMessage: "A foglalkozás kötelező."
	},
	stampNumberRequired: {
		id: "form.user_profile.stamp_number.error.required",
		defaultMessage: "A pecsétszám kötelező."
	},
	stampNumberFormat: {
		id: "form.user_profile.stamp_number.error.format",
		defaultMessage: "A pecsétszám formátuma nem megfelelő."
	},
	languageRequired: {
		id: "form.user_profile.language.error.required",
		defaultMessage: "A nyelv kötelező."
	},
	passwordRequired: {
		id: "form.user_profile.password.error.required",
		defaultMessage: "A jelszó kötelező."
	},
	passwordMinLength: {
		id: "form.user_profile.password.error.min_length",
		defaultMessage: "A jelszó minimalális hossza {minLength}."
	},
	passwordConst: {
		id: "form.user_profile.password.error.const",
		defaultMessage: "A jelszavak nem egyeznek meg."
	}
});

const errorMessages = {
	firstName: { presence: errors.firstNameRequired },
	lastName: { presence: errors.lastNameRequired },
	phoneNumber: {
		presence: errors.phoneNumberRequired,
		format: errors.phoneNumberFormat
	},
	occupation: { presence: errors.occupationRequired },
	stampNumber: {
		presence: errors.stampNumberRequired,
		format: errors.stampNumberFormat
	},
	language: { presence: errors.languageRequired },
	password: {
		presence: errors.passwordRequired,
		minLength: errors.passwordMinLength,
		const: errors.passwordConst
	}
};

const fields: {
	[key: string]: FieldData;
} = {
	title: {
		type: "field",
		path: ["title"],
		label: messages.title,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	},
	firstName: {
		type: "field",
		path: ["firstName"],
		label: messages.firstName,
		errorMessages: errorMessages.firstName,
		input: { type: "input", props: { type: "text" } }
	},
	lastName: {
		type: "field",
		path: ["lastName"],
		label: messages.lastName,
		errorMessages: errorMessages.lastName,
		input: { type: "input", props: { type: "text" } }
	},
	phoneNumber: {
		type: "field",
		path: ["phoneNumber"],
		label: messages.phoneNumber,
		errorMessages: errorMessages.phoneNumber,
		input: { type: "input", props: { type: "phone-number" } }
	},
	occupation: {
		type: "field",
		path: ["occupation"],
		label: messages.occupation,
		errorMessages: errorMessages.occupation,
		input: { type: "select", props: { labels: occupationMessages } }
	},
	stampNumber: {
		type: "field",
		path: ["stampNumber"],
		label: messages.stampNumber,
		errorMessages: errorMessages.stampNumber,
		input: { type: "input", props: { type: "text" } }
	},
	language: {
		type: "field",
		path: ["defaultLanguage"],
		label: messages.language,
		errorMessages: errorMessages.language,
		input: { type: "select", props: { labels: languageMessages } }
	},
	currentPassword: {
		type: "field",
		path: ["currentPassword"],
		label: messages.currentPassword,
		errorMessages: {},
		input: {
			type: "input",
			props: { type: "password", autoComplete: "new-password" }
		}
	},
	password: {
		type: "field",
		path: ["newPassword"],
		label: messages.newPassword,
		errorMessages: errorMessages.password,
		input: {
			type: "input",
			props: { type: "password", autoComplete: "new-password" }
		}
	},
	passwordAgain: {
		type: "field",
		path: ["newPasswordAgain"],
		label: messages.newPasswordAgain,
		errorMessages: errorMessages.password,
		input: {
			type: "input",
			props: { type: "password", autoComplete: "new-password" }
		}
	}
};

const profileFieldset: FieldsetData = {
	rows: [
		{
			columns: [
				{ width: 2, field: fields.title },
				{ width: 5, field: fields.lastName },
				{ width: 5, field: fields.firstName }
			]
		},
		{ columns: [{ width: 12, field: fields.phoneNumber }] },
		{ columns: [{ width: 12, field: fields.occupation }] },
		{ columns: [{ width: 12, field: fields.stampNumber }] },
		{ columns: [{ width: 12, field: fields.language }] }
	]
};

const passwordFieldset: FieldsetData = {
	rows: [
		{ columns: [{ width: 12, field: fields.currentPassword }] },
		{
			columns: [
				{ width: 6, field: fields.password },
				{ width: 6, field: fields.passwordAgain }
			]
		}
	]
};

export const UserProfile: React.FunctionComponent = () => {
	const [isLoading, initialValue] = useInitialValue();
	if (isLoading || initialValue === null) {
		return <FullPagePreloader />;
	}
	return <Content1 initialValue={initialValue} />;
};

const Content1: React.FunctionComponent<{ initialValue: Value }> = ({
	initialValue
}) => {
	const { user } = useAuthState();
	const [isLoading, authType] = useAuthType(user === null ? "" : user.email);
	if (isLoading || authType === null) {
		return <FullPagePreloader />;
	}
	return <Content2 initialValue={initialValue} authType={authType} />;
};

const Content2: React.FunctionComponent<{
	initialValue: Value;
	authType: AuthType;
}> = ({ initialValue, authType }) => {
	const { formState, isSubmitted, isLoading, onSubmit } = useForm(
		initialValue,
		authType === AuthType.Passwordless ? passwordlessValidator : validator
	);
	return (
		<FormPage variant="narrow">
			<Card>
				<CardHeader>
					<CardTitle>
						<FormattedMessage {...messages.userProfile} />
					</CardTitle>
				</CardHeader>
				<CardContent>
					<FormContextProvider {...formState}>
						<Form isSubmitted={isSubmitted} onSubmit={onSubmit}>
							<FormBody>
								<Fieldset>
									<Legend>
										<FormattedMessage
											{...messages.personalDetails}
										/>
									</Legend>
									<FieldsetRenderer {...profileFieldset} />
								</Fieldset>
								{authType !== AuthType.Passwordless && (
									<Fieldset>
										<Legend>
											<FormattedMessage
												{...messages.securityDetails}
											/>
										</Legend>
										<FieldsetRenderer
											{...passwordFieldset}
										/>
									</Fieldset>
								)}
							</FormBody>
							<FormFooter>
								<SplitRow>
									<SplitColumn side="right">
										<Button
											type="submit"
											isLoading={isLoading}
										>
											<FormattedMessage
												{...genericMessages.save}
											/>
										</Button>
									</SplitColumn>
								</SplitRow>
							</FormFooter>
						</Form>
					</FormContextProvider>
				</CardContent>
			</Card>
		</FormPage>
	);
};
